import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Check, Star, ArrowRight } from 'lucide-react';

export const plans = [
  {
    id: 'three-month-subscription',
    name: 'Students Impact Assessment Plan',
    tag: 'Most Popular',
    price: '₹2499',
    amount: 2499,
    period: '/student',
    desc: 'An annual school plan combining assessments, workshops, counselling, and impact reporting.',
    for: 'Schools & Student Groups',
    assessments: [
      'Two student workshops: (career guidance and psychological wellbeing) Baseline and annual(any time after 3 months of baseline assessment)',
      'AI-enabled one baseline assessment and one Annual impact assessment',
    ],
    counseling: 'Group counselling support for students',
    features: [
      'Individual student baseline assessment report',
      'One Teacher and parent engagement workshop after baseline reporting',
      'Recommendations for students, parents, and the institute',
      'Annual project impact report with indicators, dashboard, and recommendations',
    ],
    color: '#2A9D8F',
    recommended: true,
    contactOnly: true,
    schoolWorkflow: 'manual-sharing',
    requiresSchoolName: true,
    contactHref: 'mailto:mindvrik@gmail.com?subject=Students%20Impact%20Assessment%20Plan',
  },
   {
    id: 'individual-assessment',
    name: 'Baseline Assessment',
    tag: 'Individual',
    price: '₹1799',
    amount: 1799,
    period: '/student',
    desc: 'Personalised career and wellbeing support for an individual student.',
    for: 'Students & Parents',
    assessments: ['Career guidance or psychological wellbeing assessment'],
    counseling: 'Pre-assessment counselling session',
    features: [
      'Individual assessment report',
      'Post-assessment counselling session',
      'Career or wellbeing action plan based on the assessment',
      'Parent interaction, where required',
    ],
    color: '#8DA399',
    recommended: false,
  },
];

const Plans = ({ onOpenAssessment, onSelectPlan }) => {
  const [ref, visible] = useScrollReveal();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [contactPlan, setContactPlan] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  const [coordinatorEmail, setCoordinatorEmail] = useState('');
  const [studentCount, setStudentCount] = useState('');
  const [grades, setGrades] = useState('');
  const [contactError, setContactError] = useState('');
  const handlePlanSelect = (plan) => {
    if (plan.contactOnly) {
      setContactPlan(plan);
      setSchoolName('');
      setCoordinatorEmail('');
      setStudentCount('');
      setGrades('');
      setContactError('');
      return;
    }
    if (onSelectPlan) {
      onSelectPlan(plan);
      return;
    }
    onOpenAssessment?.();
  };

  const contactSchoolPlan = () => {
    if (!schoolName.trim()) {
      setContactError('Please enter your school name.');
      return;
    }
    if (!coordinatorEmail.trim() || !coordinatorEmail.includes('@')) {
      setContactError('Please enter a valid coordinator email address.');
      return;
    }
    if (!studentCount.trim() || Number(studentCount) < 1) {
      setContactError('Please enter the expected number of students.');
      return;
    }
    const separator = contactPlan.contactHref.includes('?') ? '&' : '?';
    const workflow = contactPlan.schoolWorkflow === 'manual-sharing'
      ? 'Annual plan workflow: after MindVrik confirms the programme and enrolment, the coordinator will manually share the common assessment URL with enrolled students.'
      : 'Bulk assessment enquiry: please contact us with programme scope, pricing, scheduling, and onboarding details.';
    const body = encodeURIComponent([
      `School Name: ${schoolName.trim()}`,
      `Coordinator Email: ${coordinatorEmail.trim()}`,
      `Expected Students: ${studentCount.trim()}`,
      `Grades / Classes: ${grades.trim() || 'To be confirmed'}`,
      `Plan: ${contactPlan.name}`,
      '',
      workflow,
    ].join('\n'));
    window.location.href = `${contactPlan.contactHref}${separator}body=${body}`;
  };

  return (
    <section
      id="plans"
      ref={ref}
      style={{ padding: '100px 24px', background: '#F9F9F7' }}
      data-testid="plans-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">Services & Plans</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            Choose Your <span style={{ color: '#2A9D8F' }}>Growth Plan</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '560px', margin: '0 auto', lineHeight: 1.7 }}>
            From individual career discovery to school group diagnostics, choose the guidance format that fits your next decision.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '22px',
          alignItems: 'stretch',
        }}>
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={`reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''} ${plan.recommended ? 'plan-recommended' : 'card-lift'}`}
              data-testid={`plan-card-${i}`}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                borderRadius: '24px',
                padding: plan.recommended ? '36px 28px' : '32px 24px',
                background: plan.recommended ? undefined : 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(16px)',
                border: plan.recommended ? 'none' : `1.5px solid ${plan.color}28`,
                boxShadow: plan.recommended ? undefined : '0 4px 20px rgba(0,0,0,0.04)',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                position: 'relative',
                minHeight: '100%',
                transform: hoveredIndex === i && !plan.recommended ? 'translateY(-6px)' : undefined,
              }}
            >
              {plan.recommended && (
                <div style={{
                  position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                  background: '#F4A261', color: 'white', borderRadius: '99px',
                  padding: '4px 16px', fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700,
                  letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: '4px',
                  whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(244,162,97,0.4)',
                }}>
                  <Star size={10} fill="white" /> RECOMMENDED
                </div>
              )}

              <div style={{
                fontSize: '10px', fontFamily: 'Manrope', fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: plan.recommended ? 'rgba(255,255,255,0.72)' : plan.color,
                marginBottom: '8px',
              }}>{plan.tag}</div>

              <h3 style={{
                fontFamily: 'Manrope', fontWeight: 700, fontSize: '18px',
                color: plan.recommended ? 'white' : '#1A202C',
                margin: '0 0 6px',
              }}>{plan.name}</h3>

              <div style={{ margin: '16px 0', display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                <span style={{
                  fontFamily: 'Manrope', fontWeight: 800, fontSize: '30px',
                  color: plan.recommended ? 'white' : '#1A202C',
                  letterSpacing: '-0.02em',
                }}>{plan.price}</span>
                <span style={{ fontSize: '13px', color: plan.recommended ? 'rgba(255,255,255,0.7)' : '#64748B', marginLeft: '2px' }}>{plan.period}</span>
              </div>

              <p style={{ fontSize: '13px', color: plan.recommended ? 'rgba(255,255,255,0.82)' : '#64748B', lineHeight: 1.6, margin: '0 0 20px' }}>
                {plan.desc}
              </p>

              <div style={{ height: '1px', background: plan.recommended ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)', margin: '0 0 20px' }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                {[...plan.assessments, plan.counseling, ...plan.features].map((item, idx) => (
                  <div key={`${item}-${idx}`} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Check size={13} color={plan.recommended ? 'rgba(255,255,255,0.9)' : '#2A9D8F'} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: plan.recommended ? 'rgba(255,255,255,0.84)' : '#4A5568', lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </div>

              <button
                data-testid={`plan-cta-${i}`}
                onClick={() => handlePlanSelect(plan)}
                style={{
                  width: '100%', padding: '12px', borderRadius: '14px', cursor: 'pointer',
                  background: plan.recommended ? 'rgba(255,255,255,0.22)' : `${plan.color}15`,
                  border: plan.recommended ? '1.5px solid rgba(255,255,255,0.35)' : `1.5px solid ${plan.color}40`,
                  color: plan.recommended ? 'white' : plan.color,
                  fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  transition: 'transform 0.2s ease, background 0.2s ease, gap 0.2s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = plan.recommended ? 'rgba(255,255,255,0.3)' : `${plan.color}25`;
                  e.currentTarget.style.gap = '10px';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = plan.recommended ? 'rgba(255,255,255,0.22)' : `${plan.color}15`;
                  e.currentTarget.style.gap = '6px';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {plan.schoolWorkflow === 'enquiry' ? 'Request Bulk Assessment' : plan.schoolWorkflow === 'manual-sharing' ? 'Set Up Annual Plan' : 'Start This Plan'} <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
      {contactPlan && (
        <div role="dialog" aria-modal="true" aria-label="Contact MindVrik for a school programme" style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'grid', placeItems: 'center', padding: '24px', background: 'rgba(15, 23, 42, 0.55)' }}>
          <div style={{ width: 'min(100%, 440px)', borderRadius: '22px', background: 'white', padding: '30px', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
            <div className="section-label">{contactPlan.schoolWorkflow === 'manual-sharing' ? 'Annual Plan Setup' : 'Bulk Assessment Enquiry'}</div>
            <h3 style={{ margin: '12px 0 8px', color: '#1A202C', fontFamily: 'Manrope' }}>{contactPlan.name}</h3>
            <p style={{ margin: '0 0 20px', color: '#64748B', lineHeight: 1.6 }}>
              {contactPlan.schoolWorkflow === 'manual-sharing'
                ? 'Share your rollout details. After MindVrik confirms the annual programme, the school coordinator will manually share the common assessment URL with enrolled students.'
                : 'Share your requirements and our team will contact you about programme scope, scheduling, and custom pricing.'}
            </p>
            <label style={{ display: 'block', marginBottom: '18px', color: '#334155', fontWeight: 700, fontSize: '14px' }}>
              School Name
              <input value={schoolName} onChange={(event) => { setSchoolName(event.target.value); setContactError(''); }} placeholder="Enter your school name" style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '8px', border: '1px solid #9EDBD4', borderRadius: '11px', padding: '12px', font: 'inherit', outline: 'none' }} />
              {contactError && <small style={{ display: 'block', color: '#C05621', marginTop: '6px', fontWeight: 500 }}>{contactError}</small>}
            </label>
            <label style={{ display: 'block', marginBottom: '18px', color: '#334155', fontWeight: 700, fontSize: '14px' }}>
              Coordinator Email
              <input type="email" value={coordinatorEmail} onChange={(event) => { setCoordinatorEmail(event.target.value); setContactError(''); }} placeholder="coordinator@school.edu" style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '8px', border: '1px solid #9EDBD4', borderRadius: '11px', padding: '12px', font: 'inherit', outline: 'none' }} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '18px' }}>
              <label style={{ display: 'block', color: '#334155', fontWeight: 700, fontSize: '14px' }}>
                Expected Students
                <input type="number" min="1" value={studentCount} onChange={(event) => { setStudentCount(event.target.value); setContactError(''); }} placeholder="e.g. 250" style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '8px', border: '1px solid #9EDBD4', borderRadius: '11px', padding: '12px', font: 'inherit', outline: 'none' }} />
              </label>
              <label style={{ display: 'block', color: '#334155', fontWeight: 700, fontSize: '14px' }}>
                Grades / Classes
                <input value={grades} onChange={(event) => setGrades(event.target.value)} placeholder="e.g. 9–12" style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '8px', border: '1px solid #9EDBD4', borderRadius: '11px', padding: '12px', font: 'inherit', outline: 'none' }} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-ghost" onClick={() => setContactPlan(null)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={contactSchoolPlan}>{contactPlan.schoolWorkflow === 'manual-sharing' ? 'Send Setup Request' : 'Send Enquiry'} <ArrowRight size={15} /></button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Plans;
